import React, { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { ExpandIcon } from "../assets/icon-file";
import CustomAudioVideoControls from "./CustomAudioVideoControls";

const CustomVideoPlayer = () => {
  const [searchParams] = useSearchParams();
  const url = searchParams.get("url");
  const time = searchParams.get("t") || "";
  const videoUrl = `${url}${time ? `#t=${time}` : ""}`;
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [seeking, setIsSeeking] = useState(false);
  const [playInline, setPlayInline] = useState(true);
  const [showExpand, setShowExpand] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement !== null);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    // Duration is tied to a useEffect so that the media and duration are available when the component mounts
    const videoElement = videoRef.current;
    const handleLoadedMetadata = () => {
      setDuration(videoElement.duration);
    };
    function attachListeners() {
      videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);
    }
    if (!videoElement) {
      // If no video yet, wait for it to appear in the DOM
      const intervalId = setInterval(() => {
        if (videoElement) {
          clearInterval(intervalId);
          attachListeners();
        }
      }, 100);
    } else {
      attachListeners();
    }
    //videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);
    return () => {
      videoElement.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, []);

  useEffect(() => {
    if (!playInline) {
      async function expandScreen() {
        // The play command initiates the fullscreen mode. Capture currentTime to avoid changing where the video is at when the screen expands.
        const timeStamp = currentTime;
        if (!videoRef.current.paused) {
          videoRef.current.pause();
          videoRef.current.currentTime = timeStamp;
          videoRef.current.play();
          setPlayInline(true);
        } else {
          await videoRef.current.play();
          videoRef.current.pause();
          videoRef.current.currentTime = timeStamp;
          setPlayInline(true);
        }
      }
      expandScreen();
    }
  }, [playInline]);

  const handlePlayInline = () => {
    const videoElement = videoRef.current;
    if (!videoElement.fullscreenElement) {
      if (videoElement.requestFullscreen) {
        // Chrome, Firefox, Safari
        videoElement.requestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
      } else if (videoElement.msRequestFullscreen) {
        // IE11
        videoElement.msRequestFullscreen();
      } else {
        // IPhone requires a work around when using customized controls
        setPlayInline(false);
      }
    }
  };

  const handleExpand = () => {
    setShowExpand(true);
    setTimeout(() => {
      setShowExpand(false);
    }, 3000);
  };

  const handleTimeUpdate = () => {
    setCurrentTime(videoRef.current.currentTime);
  };

  return (
    <div className="flex items-center justify-center">
      <div className="relative w-full h-full">
        <video
          ref={videoRef}
          src={videoUrl}
          controls={isFullscreen ?? true}
          id="videoPlayer"
          playsInline={playInline}
          preload="metadata"
          onTimeUpdate={() => {
            // Updating while dragging is handled in the handleSeeking function
            if (!seeking) {
              handleTimeUpdate();
            }
          }}
          onPlaying={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onMouseEnter={() => handleExpand()}
          onClick={() => handleExpand()}
          className="w-full aspect-video bg-[#030303] rounded-t-xl"
        />
        {showExpand && (
          <div
            onClick={(event) => handlePlayInline(event)}
            className="h-8 w-8 absolute top-2 left-2 h-4 w-4"
          >
            <ExpandIcon />
          </div>
        )}
        <div className="flex items-center bg-primaryDark text-white flex-col w-full min-h-[140px] sm:min-h-max sm:flex-row rounded-b-xl gap-7 p-4 mt-[-1px]">
          <CustomAudioVideoControls
            mediaRef={videoRef}
            mediaUrl={videoUrl}
            isPlaying={isPlaying}
            currentTime={currentTime}
            setCurrentTime={setCurrentTime}
            duration={duration}
            seeking={seeking}
            setIsSeeking={setIsSeeking}
          />
        </div>
      </div>
    </div>
  );
};

export default CustomVideoPlayer;
